/**
 * Phase 11 — Print Stations page.
 *
 * Multi-library selection → A4 PDF handout. The page mounts at
 * `/print/stations` and shows 4 collapsible sections, one per
 * station-style library (Live, Crew-Mess Live, Standard, Theme Buffet).
 * The user picks any combination of items across the sections; the
 * "Generate PDF" button fires `POST /api/library/print-stations` and
 * downloads the returned PDF using the server-suggested filename.
 */
import { useEffect, useMemo, useState } from "react";
import client from "@/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Printer, ChevronDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { printStationsPdf } from "@/lib/settingsApi";

// Canonical, ordered list of libraries this feature supports. Matches
// `ALLOWED_LIB_KEYS` in `backend/print_stations_pdf.py`.
const LIB_GROUPS = [
  {
    key: "lib_live_stations",
    label: "Live Stations",
    endpoint: "/lib/live_stations",
    accentClass: "bg-red-100 text-red-700",
    summary: (it) => `${(it.dishes || []).length} dish${(it.dishes || []).length === 1 ? "" : "es"}`,
  },
  {
    key: "lib_crew_mess_live_stations",
    label: "Crew Mess — Live Stations",
    endpoint: "/lib/crew_mess_live_stations",
    accentClass: "bg-red-100 text-red-700",
    summary: (it) => `${(it.dishes || []).length} dish${(it.dishes || []).length === 1 ? "" : "es"}`,
  },
  {
    key: "lib_standard_stations",
    label: "Standard Stations",
    endpoint: "/lib/standard_stations",
    accentClass: "bg-slate-100 text-slate-700",
    summary: (it) => `${(it.dishes || []).length} dish${(it.dishes || []).length === 1 ? "" : "es"}`,
  },
  {
    key: "lib_theme_buffets",
    label: "Theme Buffets",
    endpoint: "/lib/theme_buffets",
    accentClass: "bg-indigo-100 text-indigo-700",
    summary: (it) => {
      const h = (it.hot_kitchen || []).length;
      const c = (it.cold_kitchen || []).length;
      const p = (it.pastry || []).length;
      return `${h} hot · ${c} cold · ${p} pastry`;
    },
  },
];

export default function PrintStations() {
  const [items, setItems] = useState({});            // {libKey: [docs]}
  const [selected, setSelected] = useState({});      // {libKey: Set<id>}
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Hotfix 2 — paper size selector. Persists in localStorage so the
  // operator's last choice survives reloads.
  const [paper, setPaper] = useState(() => {
    try {
      return localStorage.getItem("vm:print_paper") || "a4";
    } catch {
      return "a4";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("vm:print_paper", paper); } catch { /* no-op */ }
  }, [paper]);

  // 1. Fetch every library group on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      LIB_GROUPS.map((g) =>
        client.get(g.endpoint).then((r) => [g.key, r.data || []]).catch(() => [g.key, []]),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      const next = Object.fromEntries(pairs);
      setItems(next);
      setSelected(Object.fromEntries(LIB_GROUPS.map((g) => [g.key, new Set()])));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // v8 — when the page is entered via a URL hash like `#lib_theme_buffets`
  // (the "Print A4 Guide" shortcut on the corresponding library page sets
  // this), scroll the matching accordion into view once the data is
  // loaded. The accordion itself defaults to all-open, so we just need
  // the smooth-scroll affordance.
  useEffect(() => {
    if (loading) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const el = document.querySelector(`[data-testid="group-${hash}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const totalSelected = useMemo(
    () => Object.values(selected).reduce((acc, s) => acc + s.size, 0),
    [selected],
  );

  const toggleOne = (libKey, id) => {
    setSelected((prev) => {
      const next = new Set(prev[libKey]);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, [libKey]: next };
    });
  };

  const toggleAllInGroup = (libKey, allSelected) => {
    setSelected((prev) => ({
      ...prev,
      [libKey]: allSelected
        ? new Set()
        : new Set((items[libKey] || []).map((it) => it.id)),
    }));
  };

  const clearAll = () => {
    setSelected(Object.fromEntries(LIB_GROUPS.map((g) => [g.key, new Set()])));
  };

  const selectAllVisible = () => {
    setSelected(
      Object.fromEntries(
        LIB_GROUPS.map((g) => [g.key, new Set((items[g.key] || []).map((it) => it.id))]),
      ),
    );
  };

  const handleGenerate = async () => {
    const selections = Object.fromEntries(
      Object.entries(selected)
        .filter(([, s]) => s.size > 0)
        .map(([k, s]) => [k, Array.from(s)]),
    );
    setGenerating(true);
    try {
      const { blob, filename } = await printStationsPdf({
        selections,
        title: title.trim() || null,
        paper,
      });
      // Trigger browser download with the server-suggested filename.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({
        title: "Station guide ready",
        description: `Downloaded ${filename} (${totalSelected} item${totalSelected !== 1 ? "s" : ""}).`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast({
        title: "Could not generate PDF",
        description: typeof detail === "string" ? detail : JSON.stringify(detail),
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading station libraries…
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-4xl space-y-6 p-6 pb-24"
      data-testid="print-stations-page"
    >
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Printer className="h-6 w-6 text-primary" /> Print Station Guide
          </h1>
          <p className="text-sm text-muted-foreground">
            Select any combination of stations across the four station-style
            libraries and download a single A4 PDF handout for the production
            team.
          </p>
        </div>
        <Badge
          variant={totalSelected > 0 ? "default" : "secondary"}
          className="shrink-0 text-sm"
          data-testid="selection-count-badge"
        >
          {totalSelected} selected
        </Badge>
      </header>

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          onClick={selectAllVisible}
          className="text-primary hover:underline"
          data-testid="select-all-visible-btn"
        >
          Select all visible
        </button>
        <span className="text-muted-foreground">·</span>
        <button
          type="button"
          onClick={clearAll}
          className="text-muted-foreground hover:underline"
          data-testid="clear-all-btn"
        >
          Clear all
        </button>
      </div>

      <Accordion
        type="multiple"
        defaultValue={LIB_GROUPS.map((g) => g.key)}
        className="space-y-3"
      >
        {LIB_GROUPS.map((g) => {
          const groupItems = items[g.key] || [];
          const sel = selected[g.key] || new Set();
          const allSelected = groupItems.length > 0 && sel.size === groupItems.length;
          return (
            <AccordionItem
              key={g.key}
              value={g.key}
              className="border rounded-md bg-card"
              data-testid={`group-${g.key}`}
            >
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center gap-3 text-left">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${g.accentClass}`}>
                    {g.label}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {groupItems.length} row{groupItems.length !== 1 ? "s" : ""}
                    {sel.size > 0 && ` · ${sel.size} selected`}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-2">
                {groupItems.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    This library is empty. Add entries in the Library page first.
                  </p>
                ) : (
                  <>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => toggleAllInGroup(g.key, allSelected)}
                        data-testid={`select-all-${g.key}`}
                      />
                      <span>Select all in {g.label}</span>
                    </label>
                    <ul className="divide-y divide-border">
                      {groupItems.map((it) => {
                        const checked = sel.has(it.id);
                        return (
                          <li
                            key={it.id}
                            className="flex items-center gap-3 py-2"
                            data-testid={`row-${g.key}-${it.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleOne(g.key, it.id)}
                              data-testid={`checkbox-${g.key}-${it.id}`}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium">{it.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {g.summary(it)}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Card data-testid="generate-card">
        <CardHeader>
          <CardTitle className="text-base">Generate PDF</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="station-guide-title" className="text-sm font-medium">
              Custom title (optional)
            </Label>
            <Input
              id="station-guide-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='e.g. "Embarkation Day — Buffet Set-up"'
              data-testid="custom-title-input"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              {/* Hotfix 2 — paper size selector. Native <select> keeps
                  the bundle lean; persists to localStorage on change. */}
              <Label htmlFor="station-paper" className="text-sm font-medium">
                Paper size
              </Label>
              <select
                id="station-paper"
                value={paper}
                onChange={(e) => setPaper(e.target.value)}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
                data-testid="paper-size-select"
              >
                <option value="a4">A4</option>
                <option value="letter">Letter</option>
              </select>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating || totalSelected === 0}
              data-testid="generate-stations-pdf-btn"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Printer className="h-4 w-4" />
              )}
              {generating ? "Generating…" : `Generate PDF (${totalSelected})`}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
